import { EditorConfig, LexicalEditor } from "lexical";
import { Decorable, GConstructor } from "./general";

export interface StickyPayload {
  x?: number;
  y?: number;
  z?: number | 'auto';
};

export interface StickyMixinPayload {
  __x?: number;
  __y?: number;
  __z?: number | 'auto';
}

export type SerializedSticky = {
  x?: number;
  y?: number;
  z?: number | 'auto';
};

type StickyCtor = GConstructor<Decorable & StickyMixinPayload>;

export function StickyMixin<TBase extends StickyCtor>(Base: TBase) {
  return class Sticky extends Base {
    __x!: number;
    __y!: number;
    __z!: number | 'auto';

    constructor(
      ...args: any[]
    ) {
      const [x, y, z, ...rest] = args;
      super(...rest);
      this.__x = x || 15;
      this.__y = y || 30;
      this.__z = z || 'auto';
    }

    createDOM(config: EditorConfig, editor: LexicalEditor) {
      const dom = super.createDOM(config, editor);
      dom.style.position = 'absolute';
      this.applyCoordinatesStyles(dom);
      return dom;
    }

    updateDOM(_prevNode: unknown, _dom: HTMLElement, _config: EditorConfig): boolean {
      super.updateDOM(_prevNode, _dom, _config);
      this.applyCoordinatesStyles(_dom);
      return false
    }

    getX() {
      const self = this.getLatest();
      return self.__x;
    }

    getY() {
      const self = this.getLatest();
      return self.__y;
    }
  
    getZ() {
      const self = this.getLatest();
      return self.__z;
    }
  
    setCoordinates(x: number, y: number, z: number | 'auto') {
      const self = this.getWritable();
      self.__x = x;
      self.__y = y;
      self.__z = z;
    }

    applyCoordinatesStyles(dom: HTMLElement) {
      const source = this.getLatest();
      dom.style.left = `${source.getX()}%`;
      dom.style.top = `${source.getY()}%`;
      dom.style.zIndex = `${source.getZ()}`;
    }
  };
}

type Sticky = InstanceType<ReturnType<typeof StickyMixin<StickyCtor>>>;

export const $isStickyNode = (node: unknown): node is Sticky => {
  return Boolean(node && typeof node === 'object' && '__x' in node && '__y' in node && '__z' in node);
}